<template>
  <v-dialog v-model="dialog" persistent scrollable width="600">
    <v-card>
      <v-card-title class="d-flex justify-space-between align-center">
        <span class="text-h6">New Bulk Upload</span>
        <v-btn icon @click="closeDialog">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-divider />

      <v-card-text class="pt-4">
        <Autocomplete
          v-if="isAdmin"
          label="Select Vendor *"
          :items="vendorOptions"
          itemText="company_title"
          itemValue="id"
          :loading="vendorLoading"
          :totalItems="vendorCount"
          @updateList="fetchVendorOptions"
          v-model="selectedVendor"
          hide-details="auto"
          class="mb-4"
        />

        <div
          class="drop-zone"
          :class="{ 'drop-zone--active': dragActive, 'drop-zone--disabled': uploadDisabled }"
          @dragover.prevent="!uploadDisabled && (dragActive = true)"
          @dragleave.prevent="dragActive = false"
          @drop.prevent="!uploadDisabled && onDrop($event)"
          @click="!uploadDisabled && $refs.fileInput.click()"
        >
          <template v-if="!file">
            <v-icon size="36" color="grey">mdi-tray-arrow-up</v-icon>
            <div class="mt-2">
              <template v-if="uploadDisabled">Select a vendor to enable upload</template>
              <template v-else>
                Drop your file here or
                <span class="primary--text font-weight-bold">browse</span>
              </template>
            </div>
            <div class="caption grey--text mt-1">
              Accepts .xlsx, .xls, or .csv files up to 10 MB
            </div>
          </template>
          <template v-else>
            <v-icon size="32" color="success">mdi-file-check-outline</v-icon>
            <div class="mt-2 font-weight-bold">{{ file.name }}</div>
            <div class="caption grey--text mt-1">
              {{ rows.length }} row(s) parsed
            </div>
            <v-btn text small color="error" class="mt-2" @click.stop="clearFile">
              Remove file
            </v-btn>
          </template>
        </div>
        <input
          ref="fileInput"
          type="file"
          accept=".xlsx,.xls,.csv"
          class="d-none"
          :disabled="uploadDisabled"
          @change="onFileSelected"
        />

        <v-alert
          v-if="duplicateGroups.length"
          type="warning"
          outlined
          dense
          class="mt-4 mb-0"
        >
          <div class="font-weight-bold mb-2">
            Duplicate container number(s) found in this file
          </div>
          <div
            v-for="group in duplicateGroups"
            :key="group.containerNumber"
            class="mb-2"
          >
            <div class="caption font-weight-bold mb-1">
              '{{ group.containerNumber }}' — {{ group.rowIndexes.length }} rows
            </div>
            <v-chip
              v-for="rowIndex in group.rowIndexes"
              :key="rowIndex"
              small
              close
              outlined
              class="mr-1 mb-1"
              @click:close="removeRow(rowIndex)"
            >
              Row {{ rowIndex + 1 }}
            </v-chip>
          </div>
          <div class="caption">
            Remove the extra row(s) for each duplicate before uploading.
          </div>
        </v-alert>

        <v-alert
          v-if="invalidDateRows.length"
          type="error"
          outlined
          dense
          class="mt-4 mb-0"
        >
          <div class="font-weight-bold mb-2">
            Invalid or missing Movement Date
          </div>
          <div class="caption mb-2">
            Movement Date must be in YYYY/MM/DD format (e.g. 2025/12/25).
          </div>
          <v-chip
            v-for="rowIndex in invalidDateRows"
            :key="rowIndex"
            small
            close
            outlined
            class="mr-1 mb-1"
            @click:close="removeRow(rowIndex)"
          >
            Row {{ rowIndex + 1 }}
          </v-chip>
        </v-alert>

        <v-card outlined class="pa-3 mt-4">
          <div class="overline grey--text mb-2">Expected Columns In Your File</div>
          <v-chip
            v-for="column in expectedColumns"
            :key="column"
            small
            outlined
            color="primary"
            class="mr-1 mb-1"
          >
            {{ column }}
          </v-chip>
        </v-card>

        <v-textarea
          v-model="notes"
          label="Notes (optional)"
          placeholder="Any remarks about this upload..."
          outlined
          dense
          rows="2"
          hide-details="auto"
          class="mt-4"
        />

        <v-btn text color="primary" class="mt-3 px-0" @click="downloadSampleFile">
          <v-icon left small>mdi-download</v-icon>
          Download sample file
        </v-btn>
      </v-card-text>

      <v-divider />
      <v-card-actions class="pa-4 d-flex justify-end">
        <v-btn text @click="closeDialog">Cancel</v-btn>
        <v-btn
          color="primary"
          depressed
          :disabled="!file || rows.length === 0 || duplicateGroups.length > 0 || invalidDateRows.length > 0"
          :loading="loading"
          @click="submitUpload"
        >
          <v-icon left small>mdi-tray-arrow-up</v-icon>
          Upload File
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { read, utils, SSF } from "xlsx";
import { downloadBlobData } from "@/utils/functions.js";
import Autocomplete from "@/components/FormBaseComponents/Autocomplete.vue";

export default {
  components: { Autocomplete },

  props: {
    value: {
      type: Boolean,
      default: false,
    },
    isAdmin: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      dragActive: false,
      file: null,
      rows: [],
      notes: "",
      loading: false,
      selectedVendor: null,
      vendorOptions: [],
      vendorCount: 0,
      vendorLoading: false,
      expectedColumns: [
        "Movement Date",
        "Container Number",
        "Container Type",
        "Source",
        "Destination",
        "Driver",
        "Vehicle",
      ],
    };
  },

  watch: {
    dialog(val) {
      if (val && this.isAdmin) {
        this.fetchVendorOptions();
      }
    },
  },

  computed: {
    dialog: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
    selectedVendorId() {
      if (!this.selectedVendor) return null;
      return typeof this.selectedVendor === "object"
        ? this.selectedVendor.id
        : this.selectedVendor;
    },
    uploadDisabled() {
      return this.isAdmin && !this.selectedVendorId;
    },
    duplicateGroups() {
      const indexesByContainer = {};
      this.rows.forEach((row, index) => {
        const containerNumber = (row.container_number || "").toString().trim();
        if (!containerNumber) return;
        if (!indexesByContainer[containerNumber]) {
          indexesByContainer[containerNumber] = [];
        }
        indexesByContainer[containerNumber].push(index);
      });

      return Object.entries(indexesByContainer)
        .filter(([, rowIndexes]) => rowIndexes.length > 1)
        .map(([containerNumber, rowIndexes]) => ({
          containerNumber,
          rowIndexes,
        }));
    },
    invalidDateRows() {
      const datePattern = /^\d{4}\/\d{2}\/\d{2}$/;
      return this.rows.reduce((invalidRows, row, index) => {
        const value = (row.movement_date || "").toString().trim();
        if (!datePattern.test(value)) {
          invalidRows.push(index);
        }
        return invalidRows;
      }, []);
    },
  },

  methods: {
    removeRow(rowIndex) {
      this.rows.splice(rowIndex, 1);
    },
    onDrop(event) {
      this.dragActive = false;
      const droppedFile = event.dataTransfer.files[0];
      if (droppedFile) {
        this.parseFile(droppedFile);
      }
    },
    onFileSelected(event) {
      const selectedFile = event.target.files[0];
      if (selectedFile) {
        this.parseFile(selectedFile);
      }
    },
    parseFile(selectedFile) {
      this.file = selectedFile;
      this.rows = [];

      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const workbook = read(e.target.result, { type: "array" });
          const sheet = workbook.Sheets[workbook.SheetNames[0]];
          const sheetRows = utils.sheet_to_row_object_array(sheet, {
            header: 0,
            defval: "",
          });

          const range = utils.decode_range(sheet["!ref"]);
          const headerRowIndex = range.s.r;
          let movementDateColIndex = -1;
          for (let c = range.s.c; c <= range.e.c; c++) {
            const headerCell = sheet[utils.encode_cell({ r: headerRowIndex, c })];
            const headerValue = headerCell ? String(headerCell.v).toLowerCase().trim() : "";
            if (headerValue === "movement date") {
              movementDateColIndex = c;
              break;
            }
          }

          this.rows = sheetRows
            .filter((row) => Object.values(row).some((value) => value !== ""))
            .map((row, index) => {
              const normalized = {};
              for (const [key, value] of Object.entries(row)) {
                const newKey = key.toString().toLowerCase().trim().replaceAll(" ", "_");
                normalized[newKey] = value;
              }
              if (movementDateColIndex !== -1) {
                const cellRef = utils.encode_cell({
                  r: headerRowIndex + 1 + index,
                  c: movementDateColIndex,
                });
                const cell = sheet[cellRef];
                if (cell && cell.t === "n" && cell.v !== undefined) {
                  // Real Excel date-serial cell - reformat from the raw
                  // serial value so the result is always yyyy/mm/dd,
                  // regardless of the source cell's own display format
                  // (e.g. mm/dd/yyyy, dd-mm-yyyy, etc.).
                  normalized.movement_date = SSF.format("yyyy/mm/dd", cell.v);
                } else if (cell && cell.t === "s" && cell.v) {
                  // Plain text cell (e.g. typed "2025/12/25" directly, or
                  // the sample template's own literal string) - use the
                  // raw value as-is; format is checked separately at submit.
                  normalized.movement_date = String(cell.v).trim();
                }
              }
              return normalized;
            });

          if (this.rows.length === 0) {
            this.$bus.$emit("showToastMessage", {
              message: "No data rows found in the file.",
              color: "error",
            });
          }
        } catch (err) {
          this.$bus.$emit("showToastMessage", {
            message: "Could not read this file. Please check the format.",
            color: "error",
          });
          this.clearFile();
        }
      };
      reader.readAsArrayBuffer(selectedFile);
    },
    clearFile() {
      this.file = null;
      this.rows = [];
      this.$refs.fileInput.value = null;
    },
    fetchVendorOptions(params = {}) {
      this.vendorLoading = true;
      params = {
        company_type: "Transporter",
        limit: 10,
        offset: 0,
        ...params,
      };
      this.$api.company
        .getCompanySelectList(params)
        .then((res) => {
          if ("count" in res) {
            this.vendorCount = res.count;
          }
          if (params.offset === 0) {
            this.vendorOptions = res.data;
          } else {
            this.vendorOptions.push(...res.data);
          }
        })
        .catch(() => {})
        .finally(() => {
          this.vendorLoading = false;
        });
    },
    downloadSampleFile() {
      const request = this.isAdmin
        ? this.$api.vendorSubHireUpload.downloadAdminSampleFile()
        : this.$api.vendorSubHireUpload.downloadSampleFile();
      request
        .then((res) => {
          downloadBlobData(res.data, "bulk_vendor_subhire_upload.xlsx");
        })
        .catch(() => {
          this.$bus.$emit("showToastMessage", {
            message: "Can't download sample file!",
            color: "error",
          });
        });
    },
    submitUpload() {
      if (!this.file || this.rows.length === 0) {
        return;
      }
      if (this.isAdmin && !this.selectedVendorId) {
        this.$bus.$emit("showToastMessage", {
          message: "Please select a vendor",
          color: "error",
        });
        return;
      }
      if (this.invalidDateRows.length > 0) {
        this.$bus.$emit("showToastMessage", {
          message: "Movement Date must be in YYYY/MM/DD format for every row. Please fix the highlighted rows.",
          color: "error",
        });
        return;
      }

      const formData = new FormData();
      formData.append("file", this.file);
      formData.append("rows", JSON.stringify(this.rows));
      if (this.notes) {
        formData.append("notes", this.notes);
      }
      if (this.isAdmin) {
        formData.append("vendor", this.selectedVendorId);
      }

      this.loading = true;
      this.$bus.$emit("showLoader", true);
      const request = this.isAdmin
        ? this.$api.vendorSubHireUpload.createAdminVendorSubHireUpload(formData)
        : this.$api.vendorSubHireUpload.createVendorSubHireUpload(formData);
      request
        .then(() => {
          this.$bus.$emit("showToastMessage", {
            message: "File uploaded successfully",
            color: "success",
          });
          this.$emit("refreshList");
          this.closeDialog();
        })
        .catch((err) => {
          const message =
            (err && err.data && (err.data.detail || err.data.non_field_errors)) ||
            "Failed to upload file";
          this.$bus.$emit("showToastMessage", {
            message: Array.isArray(message) ? message.join(", ") : message,
            color: "error",
          });
        })
        .finally(() => {
          this.loading = false;
          this.$bus.$emit("showLoader", false);
        });
    },
    closeDialog() {
      this.dialog = false;
      this.clearFile();
      this.notes = "";
      this.dragActive = false;
      this.selectedVendor = null;
    },
  },
};
</script>

<style scoped>
.drop-zone {
  border: 2px dashed #c4c4c4;
  border-radius: 8px;
  padding: 32px 16px;
  text-align: center;
  cursor: pointer;
  transition: border-color 0.15s ease, background-color 0.15s ease;
}
.drop-zone:hover,
.drop-zone--active {
  border-color: var(--v-primary-base);
  background-color: rgba(25, 118, 210, 0.04);
}
.drop-zone--disabled {
  cursor: not-allowed;
  opacity: 0.5;
}
.drop-zone--disabled:hover {
  border-color: #c4c4c4;
  background-color: transparent;
}
</style>
